import React from 'react';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import { MealPreferences, FormTexts } from '../../types/form';
import YesNoButtons from '../YesNoButtons/YesNoButtons';
import DietaryRestrictions from '../DietaryRestrictions/DietaryRestrictions';
import './AnswersSection.css';

interface AnswersSectionProps {
  mealPreferences: MealPreferences;
  dietaryRestrictions: {
    selected: string[];
  };
  texts?: FormTexts | null;
  onMealUpdate: (meal: keyof MealPreferences, value: boolean) => void;
  onToggleRestriction: (restriction: string) => void;
  onAddCustomRestriction: (restriction: string) => boolean;
  onRemoveCustomRestriction: (restriction: string) => void;
}

// Helper function to decode HTML entities and render HTML safely
const renderHtmlText = (htmlString: string | undefined): React.ReactNode => {
  if (!htmlString) return null;
  
  // Decode numeric HTML entities (e.g., &#58; -> :, &#160; -> space)
  // while preserving HTML tags
  // Note: &amp; must be decoded last to avoid double-decoding
  const decodedHtml = htmlString
    .replace(/&#(\d+);/g, (match, dec) => String.fromCharCode(parseInt(dec, 10)))
    .replace(/&#x([0-9a-fA-F]+);/g, (match, hex) => String.fromCharCode(parseInt(hex, 16)))
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');
  
  // Sanitize HTML to prevent XSS attacks
  const sanitizedHtml = DOMPurify.sanitize(decodedHtml, {
    ALLOWED_TAGS: ['div', 'span', 'a', 'br', 'p', 'strong', 'em', 'u', 'b', 'i'],
    ALLOWED_ATTR: ['href', 'title', 'target', 'rel'],
    ALLOW_DATA_ATTR: false,
  });
  
  return <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />;
};

const AnswersSection: React.FC<AnswersSectionProps> = ({
  mealPreferences,
  dietaryRestrictions,
  texts,
  onMealUpdate,
  onToggleRestriction,
  onAddCustomRestriction,
  onRemoveCustomRestriction,
}) => {
  const { t } = useTranslation();

  return (
    <section className="answers-section">
      <h2 className="section-title">{t('form.answersSection.title')}</h2>
      {texts?.['General info text'] && (
        <div className="general-info-text">
          {renderHtmlText(texts['General info text'])}
        </div>
      )}
      
      <div className="meal-preferences">
        <fieldset className="meal-item">
          <legend className="meal-label-wrapper">
            <span className="meal-label">{t('form.answersSection.breakfast')}</span>
            {texts?.['Breakfast text'] && (
              <span className="meal-text">
                {renderHtmlText(texts['Breakfast text'])}
              </span>
            )}
          </legend>
          <YesNoButtons
            value={mealPreferences.breakfast}
            onChange={(value) => onMealUpdate('breakfast', value)}
          />
        </fieldset>

        <fieldset className="meal-item">
          <legend className="meal-label-wrapper">
            <span className="meal-label">{t('form.answersSection.lunch')}</span>
            {texts?.['Lunch text'] && (
              <span className="meal-text">
                {renderHtmlText(texts['Lunch text'])}
              </span>
            )}
          </legend>
          <YesNoButtons
            value={mealPreferences.lunch}
            onChange={(value) => onMealUpdate('lunch', value)}
          />
        </fieldset>

        <fieldset className="meal-item">
          <legend className="meal-label-wrapper">
            <span className="meal-label">{t('form.answersSection.afternoonSnack')}</span>
            {texts?.['Afternoon snack text'] && (
              <span className="meal-text">
                {renderHtmlText(texts['Afternoon snack text'])}
              </span>
            )}
          </legend>
          <YesNoButtons
            value={mealPreferences.afternoonSnack}
            onChange={(value) => onMealUpdate('afternoonSnack', value)}
          />
        </fieldset>
      </div>

      <DietaryRestrictions
        selected={dietaryRestrictions.selected}
        onToggle={onToggleRestriction}
        onAddCustom={onAddCustomRestriction}
        onRemoveCustom={onRemoveCustomRestriction}
      />
    </section>
  );
};

export default AnswersSection;

